import { useState, useEffect } from 'react';
import { Box, Stack, Loader, Text, Divider } from '@mantine/core';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { HpoItem } from '@/types/hpo.types';
import {
  loadEncounterFormData,
  loadEncounterPhysicalExamination,
} from './examination.helpers';
import { useTranslation } from 'react-i18next';
import { HpoSelection } from './hpos/HpoSelection';
import { PhotoFile } from './photos/PhotoInput';
import { ConditionsList } from '@/pages/ainigma/patient/examination/conditions/ConditionsList';
import { ConditionInputItem } from '@/utils/condition.utils';
import { useConditions } from '@/hooks/useConditions';
import type { PsychomotorDevelopmentItem } from '@/types/psychomotor-development.types';
import { PsychomotorDevelopmentList } from './psychomotor-development/PsychomotorDevelopmentList';
import { PhysicalGrowthData } from '@/components/inputs/PhysicalGrowthInput';
import { PhysicalGrowthList } from './physical-growth/PhysicalGrowthList';
import { PhotosList } from './photos/PhotosList';
import { PhysicalExaminationData } from '@/components/inputs/physical-examination/types';
import { PhysicalExaminationList } from './physical-examination/PhysicalExaminationList';

export function ExaminationContent({
  patient,
  encounter,
  readonly,
}: {
  patient: Patient;
  encounter: Encounter | undefined;
  readonly?: boolean;
}) {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);
  const { loadEncounterConditions } = useConditions();

  const [selectedHpo, setSelectedHpo] = useState<HpoItem[]>([]);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [conditions, setConditions] = useState<ConditionInputItem[]>([]);
  const [psychomotorDevelopment, setPsychomotorDevelopment] = useState<
    PsychomotorDevelopmentItem[]
  >([]);
  const [physicalGrowth, setPhysicalGrowth] =
    useState<PhysicalGrowthData | null>(null);
  const [physicalExamination, setPhysicalExamination] =
    useState<PhysicalExaminationData | null>(null);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [formDataLoaded, setFormDataLoaded] = useState(false);
  const [conditionsLoaded, setConditionsLoaded] = useState(false);
  const [physicalExaminationLoaded, setPhysicalExaminationLoaded] =
    useState(false);

  // Load conditions separately using useConditions hook
  useEffect(() => {
    if (!patient?.id || !encounter?.id || conditionsLoaded) {
      return;
    }

    async function loadConditions() {
      if (!encounter) {
        return;
      }
      try {
        const result = await loadEncounterConditions(patient, encounter);
        if (result.conditions.length > 0) {
          setConditions(result.conditions);
        }
        setConditionsLoaded(true);
      } catch (_error) {
        // Error loading conditions
      }
    }

    loadConditions();
  }, [patient, encounter, conditionsLoaded, loadEncounterConditions]);

  // Load physical examination data separately
  useEffect(() => {
    if (!patient?.id || !encounter?.id || physicalExaminationLoaded) {
      return;
    }

    async function loadPhysicalExam() {
      if (!encounter) {
        return;
      }
      try {
        const result = await loadEncounterPhysicalExamination(
          medplum,
          patient,
          encounter
        );
        setPhysicalExamination(result.data);
        setPhysicalExaminationLoaded(true);
      } catch (_error) {
        // Error loading physical examination
      }
    }

    loadPhysicalExam();
  }, [patient, encounter, physicalExaminationLoaded, medplum]);

  // Load and prefill form data for current encounter (excluding conditions)
  useEffect(() => {
    if (!patient?.id || !encounter?.id || formDataLoaded) {
      return;
    }

    async function loadFormData() {
      setLoadingFormData(true);
      try {
        const data = await loadEncounterFormData(medplum, patient, encounter);

        // Prefill form fields
        if (data.hpoTerms.length > 0) {
          setSelectedHpo(data.hpoTerms);
        }
        if (data.photos.length > 0) {
          setPhotos(data.photos);
        }

        if (data.psychomotorDevelopment.length > 0) {
          setPsychomotorDevelopment(data.psychomotorDevelopment);
        }
        if (data.physicalGrowth) {
          setPhysicalGrowth(data.physicalGrowth);
        }

        setFormDataLoaded(true);
      } catch (_error) {
        // Error loading form data
      } finally {
        setLoadingFormData(false);
      }
    }

    loadFormData();
  }, [medplum, formDataLoaded, encounter, patient]);

  if (loadingFormData || !encounter) {
    return (
      <Box p="lg">
        <Stack gap="lg" align="center">
          <Loader size="lg" />
          <Text>Loading...</Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Stack gap="lg">
        <PsychomotorDevelopmentList
          items={psychomotorDevelopment}
          onChange={setPsychomotorDevelopment}
          patient={patient}
          encounter={encounter}
          readonly={readonly}
        />

        <Divider />

        <PhysicalGrowthList
          data={physicalGrowth}
          onChange={setPhysicalGrowth}
          patient={patient}
          encounter={encounter}
          readonly={readonly}
        />

        <Divider />

        <PhysicalExaminationList
          data={physicalExamination}
          onChange={setPhysicalExamination}
          patient={patient}
          encounter={encounter}
        />

        <Divider />

        <Text size="xl" fw={500}>
          {t('examination.content.hpo.title', {
            ns: 'patient-pages',
          })}
        </Text>

        <HpoSelection
          patient={patient}
          encounter={encounter}
          selectedHpo={selectedHpo}
          setSelectedHpo={setSelectedHpo}
          readonly={readonly}
        />

        <Divider />

        <Text size="xl" fw={500}>
          {t('examination.content.conditions', {
            ns: 'patient-pages',
            defaultValue: 'Conditions',
          })}
        </Text>

        <ConditionsList
          conditions={conditions}
          onChange={setConditions}
          patient={patient}
          encounter={encounter}
          isMedicalHistory={false}
          readonly={readonly}
        />

        <Divider />

        <PhotosList
          photos={photos}
          onChange={setPhotos}
          patient={patient}
          encounter={encounter}
          maxPhotos={5}
          readonly={readonly}
        />
      </Stack>
    </Box>
  );
}
